import { Add, Delete, Edit, EventBusy } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import PayrollService from '../../services/payroll';
import { formatDateAR, parseDateInput } from './payroll.utils';

const emptyForm = {
  _id: null,
  name: '',
  date: '',
  multiplier: 1,
  description: '',
};

const validateHoliday = (form) => {
  const errors = {};
  if (!form.name?.trim()) errors.name = 'El nombre es obligatorio';
  if (!form.date) errors.date = 'La fecha es obligatoria';
  if (!form.multiplier || Number(form.multiplier) < 1) {
    errors.multiplier = 'El multiplicador debe ser mayor o igual a 1';
  }
  return errors;
};

const buildPayload = (form) => ({
  name: form.name.trim(),
  date: new Date(`${form.date}T00:00:00`).toISOString(),
  multiplier: Number(form.multiplier),
  description: form.description?.trim() || '',
});

function PayrollHolidaysPage({ storeId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [feedback, setFeedback] = useState({ type: null, message: '' });

  const sortedRows = useMemo(() => (
    [...rows].sort((a, b) => new Date(a.date) - new Date(b.date))
  ), [rows]);

  const loadData = async () => {
    if (!storeId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await PayrollService.listHolidays(storeId);
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setError('No se pudieron cargar los feriados.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setForm(emptyForm);
    setFormErrors({});
    setOpenForm(true);
  };

  const openEdit = (holiday) => {
    setForm({
      _id: holiday._id,
      name: holiday.name || '',
      date: parseDateInput(holiday.date),
      multiplier: holiday.multiplier ?? 1,
      description: holiday.description || '',
    });
    setFormErrors({});
    setOpenForm(true);
  };

  const handleSave = async () => {
    const errors = validateHoliday(form);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0 || !storeId) return;

    setSaving(true);
    setFeedback({ type: null, message: '' });
    try {
      const payload = buildPayload(form);
      if (form._id) {
        await PayrollService.updateHoliday(storeId, form._id, payload);
        setFeedback({ type: 'success', message: 'Feriado actualizado.' });
      } else {
        await PayrollService.createHoliday(storeId, payload);
        setFeedback({ type: 'success', message: 'Feriado creado.' });
      }
      setOpenForm(false);
      setForm(emptyForm);
      await loadData();
    } catch {
      setFeedback({ type: 'error', message: 'No se pudo guardar el feriado.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!storeId || !toDelete?._id) return;
    setDeleting(true);
    setFeedback({ type: null, message: '' });
    try {
      await PayrollService.deleteHoliday(storeId, toDelete._id);
      setFeedback({ type: 'success', message: 'Feriado eliminado.' });
      setToDelete(null);
      await loadData();
    } catch {
      setFeedback({ type: 'error', message: 'No se pudo eliminar el feriado.' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight={700}>Feriados</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Crear feriado</Button>
      </Box>

      {feedback.type && (
        <Alert severity={feedback.type} sx={{ mb: 2 }} onClose={() => setFeedback({ type: null, message: '' })}>
          {feedback.message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={loadData}>Reintentar</Button>}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Multiplicador</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Descripción</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && Array.from({ length: 5 }).map((_, idx) => (
              <TableRow key={`sk-${idx}`}>
                <TableCell><Skeleton width={110} /></TableCell>
                <TableCell><Skeleton width={160} /></TableCell>
                <TableCell><Skeleton width={70} /></TableCell>
                <TableCell><Skeleton width={190} /></TableCell>
                <TableCell align="right"><Skeleton width={70} sx={{ ml: 'auto' }} /></TableCell>
              </TableRow>
            ))}

            {!loading && !error && sortedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <Stack spacing={1} alignItems="center">
                    <EventBusy color="disabled" />
                    <Typography color="text.secondary">No hay feriados configurados</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}

            {!loading && !error && sortedRows.map((row) => (
              <TableRow hover key={row._id}>
                <TableCell>{formatDateAR(row.date)}</TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>x{Number(row.multiplier || 1)}</TableCell>
                <TableCell>{row.description || '—'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(row)} title="Editar">
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setToDelete(row)} title="Eliminar">
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={openForm} onClose={() => !saving && setOpenForm(false)} fullWidth maxWidth="sm">
        <DialogTitle>{form._id ? 'Editar feriado' : 'Nuevo feriado'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={0.5}>
            <TextField
              autoFocus
              label="Nombre"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              error={!!formErrors.name}
              helperText={formErrors.name}
              required
              fullWidth
            />
            <TextField
              label="Fecha"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.date}
              onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
              error={!!formErrors.date}
              helperText={formErrors.date}
              required
              fullWidth
            />
            <TextField
              label="Multiplicador"
              type="number"
              inputProps={{ min: 1, step: 0.1 }}
              value={form.multiplier}
              onChange={(e) => setForm((prev) => ({ ...prev, multiplier: e.target.value }))}
              error={!!formErrors.multiplier}
              helperText={formErrors.multiplier}
              required
              fullWidth
            />
            <TextField
              label="Descripción"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!toDelete} onClose={() => !deleting && setToDelete(null)}>
        <DialogTitle>Eliminar feriado</DialogTitle>
        <DialogContent dividers>
          <Typography>
            ¿Seguro que querés eliminar el feriado "{toDelete?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)} disabled={deleting}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PayrollHolidaysPage;
